import React, { useContext } from 'react';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

// Roles that can download PDFs
const ALLOWED_ROLES = [
  'complex_manager',
  'network_admin',
  'store_manager',
  'stocktake_controller',
  'stocktake_auditor'
];

const PdfDownloadButton = ({ contentId, filename, title }) => {
  const { user } = useContext(AuthContext);
  
  // Check if user has permission to download
  const canDownload = user && ALLOWED_ROLES.includes(user.role);
  
  if (!canDownload) {
    return null;
  }

  const handleDownload = async () => {
    const element = document.getElementById(contentId);
    if (!element) {
      toast.error('Content not found');
      return;
    }

    toast.info('Generating PDF...');

    try {
      // Dynamically import html2pdf to avoid SSR issues
      const html2pdf = (await import('html2pdf.js')).default;
      
      // Create a wrapper for PDF generation
      const wrapper = document.createElement('div');
      wrapper.style.padding = '20px';
      wrapper.style.backgroundColor = 'white';
      wrapper.style.color = '#1e293b';
      wrapper.style.fontFamily = 'Arial, sans-serif';
      wrapper.style.width = '800px';
      
      // Add title and date header
      const header = document.createElement('div');
      header.innerHTML = `
        <div style="text-align: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #0d9488;">
          <h1 style="font-size: 24px; font-weight: bold; color: #1e293b; margin: 0;">${title}</h1>
          <p style="font-size: 12px; color: #64748b; margin-top: 5px;">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          <p style="font-size: 12px; color: #64748b;">Generated by: ${user?.name || 'Unknown'}</p>
        </div>
      `;
      wrapper.appendChild(header);
      
      // Clone the element
      const clonedElement = element.cloneNode(true);
      
      // Apply inline styles to make sure they render in PDF
      clonedElement.style.color = '#1e293b';
      clonedElement.style.backgroundColor = 'white';
      
      // Remove any buttons and interactive elements from the clone
      clonedElement.querySelectorAll('button, .no-print, [data-testid="pdf-download-btn"]').forEach(el => el.remove());
      
      // Convert Tailwind classes to inline styles for key elements
      clonedElement.querySelectorAll('[class*="bg-"]').forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        el.style.backgroundColor = computedStyle.backgroundColor;
      });
      
      clonedElement.querySelectorAll('[class*="text-"]').forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        el.style.color = computedStyle.color;
      });
      
      clonedElement.querySelectorAll('[class*="border"]').forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        el.style.border = computedStyle.border;
        el.style.borderRadius = computedStyle.borderRadius;
      });
      
      wrapper.appendChild(clonedElement);
      
      // Temporarily append to body for rendering (visible for computation)
      wrapper.style.position = 'fixed';
      wrapper.style.left = '0';
      wrapper.style.top = '0';
      wrapper.style.zIndex = '-9999';
      wrapper.style.opacity = '1';
      document.body.appendChild(wrapper);
      
      // Wait for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${filename}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          scrollY: 0,
          windowWidth: 800,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { mode: 'avoid-all', before: '.page-break' }
      };

      await html2pdf().set(opt).from(wrapper).save();
      
      // Clean up
      document.body.removeChild(wrapper);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF: ' + error.message);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      variant="outline"
      className="gap-2 border-teal-200 text-teal-700 hover:bg-teal-50"
      data-testid="pdf-download-btn"
    >
      <Download className="w-4 h-4" />
      Download PDF
    </Button>
  );
};

export default PdfDownloadButton;
